/**
 * Simple fetch-and-set hook for search fields that don't need pagination.
 *
 * **Why this exists:**
 * Search components (SearchEvent, SearchProduct, SearchMembership) support
 * two data sources:
 *   1. A paginated service (e.g. useVenueEventsService via react-query)
 *   2. A plain async function (`fetchOptions`)
 *
 * This hook wraps option 2 so it returns the same shape as option 1,
 * letting `useSearchSharedState` switch between them with a single toggle:
 *
 *   // useSearchSharedState.ts (the only consumer of this hook)
 *   const useFetchOpts = Boolean(fetchOptions && !service);
 *   const fetchOptionsResult = useSearchWithFetchOptions({ fetchOptions, debouncedQuery });
 *   const { items, isFetching, ... } = useFetchOpts ? fetchOptionsResult : serviceResult;
 *
 * **How it gets called in practice:**
 * A consumer passes `fetchOptions` to a search component:
 *
 *   // Form config (SearchFormFields)
 *   { type: FORM_FIELD_TYPES.SEARCH, fetchOptions: (q) => venueRepository.list({ q }) }
 *
 *   // Standalone
 *   <SearchEvent fetchOptions={(q) => eventRepo.search(q)} ... />
 *
 * The search component passes that through to `useSearchSharedState`,
 * which calls this hook internally. No one calls this hook directly.
 */
import { useState, useEffect } from "react";

interface UseSearchWithFetchOptionsParams<T> {
  fetchOptions: (query: string) => Promise<T[]>;
  debouncedQuery: string;
  minQueryLength?: number;
}

interface UseSearchWithFetchOptionsResult<T> {
  items: T[];
  isFetching: boolean;
  hasNextPage: false;
  fetchNextPage: () => void;
  isFetchingNextPage: false;
}

const NOOP = (): void => {
  // Intentional no-op
};

export default function useSearchWithFetchOptions<
  T extends { id: number | string }
>({
  fetchOptions,
  debouncedQuery,
  minQueryLength = 2,
}: UseSearchWithFetchOptionsParams<T>): UseSearchWithFetchOptionsResult<T> {
  const [items, setItems] = useState<T[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (debouncedQuery.length < minQueryLength) {
      setItems([]);
      return undefined;
    }

    let cancelled = false;
    setIsFetching(true);

    fetchOptions(debouncedQuery)
      .then((results) => {
        if (!cancelled) {
          setItems(results);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setItems([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsFetching(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, minQueryLength]);

  return {
    items,
    isFetching,
    hasNextPage: false,
    fetchNextPage: NOOP,
    isFetchingNextPage: false,
  };
}
